import { EditOutlined } from "@ant-design/icons";
import { Typography } from "antd";
import {
    useDestroyIntegrationCustomFieldMutation,
    useGetIntegrationCustomFieldsQuery,
    useUpdateIntegrationCustomFieldMutation,
} from "api/TenantSlug/IntegrationCustomField/hooks.ts";
import { AddApiToolCustomFieldDrawer } from "components/Drawers/AddApiToolCustomFieldDrawer/AddApiToolCustomFieldDrawer";
import { DeleteModal } from "components/other/DeleteModal/DeleteModal.tsx";
import { Table } from "components/UI/Table";
import { tableAtom } from "components/UI/Table/atoms/tableAtom";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableAction, TableColumnType } from "components/UI/Table/types/table.ts";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { useDrawer } from "hooks/useDrawer.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import IntegrationCustomFieldData = App.Data.IntegrationCustomFieldData;
import GetIntegrationCustomFieldsResponse = App.Http.Responses.Integration.GetIntegrationCustomFieldsResponse;
import IntegrationData = App.Data.IntegrationData;

interface ApiToolCustomFieldsTableProps {
    integration: IntegrationData;
}

interface DeleteModalState {
    isOpen: boolean;
    field: IntegrationCustomFieldData | null;
}

export const ApiToolCustomFieldsTable: FC<ApiToolCustomFieldsTableProps> = ({ integration }) => {
    const tableId = TableId.ApiToolCustomFieldsTable;
    const tableAtomInstance = tableAtom<IntegrationCustomFieldData>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const { t } = usePaldockTranslation("tables.api-tool-custom-fields");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tU } = usePaldockTranslation("tables.ui");
    const { t: tC } = usePaldockTranslation("common");
    const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({ isOpen: false, field: null });
    const { show } = useDrawer();

    const getIntegrationsQuery = useGetIntegrationCustomFieldsQuery(integration?.id || "", {
        // page: {
        //     number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
        //     size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        // },
        // filter: {
        //     filter: tableState?.filter?.filter,
        //     sort: tableState?.filter?.sort,
        // },
        enabled: false,
    });

    const updateIntegrationCustomFieldMutation = useUpdateIntegrationCustomFieldMutation(
        integration?.id || "",
        tableState?.editingRowId || ""
    );
    const deleteIntegrationCustomFieldMutation = useDestroyIntegrationCustomFieldMutation(integration?.id || "");

    const tableFetchDataUtils = useTableDataApiOperations<GetIntegrationCustomFieldsResponse>(
        tableId,
        getIntegrationsQuery,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        deleteIntegrationCustomFieldMutation,
        updateIntegrationCustomFieldMutation
    );

    const handleUpdate = (record: IntegrationCustomFieldData) => {
        const { integration, ...updatePayload } = record;
        console.log(integration);

        tableFetchDataUtils.editTableData(updatePayload);
    };

    const handleDelete = (customFieldId?: string) => {
        if (!customFieldId) return;

        deleteIntegrationCustomFieldMutation?.mutate(customFieldId, {
            onSuccess: () => {
                toast.success(tU("messages.delete_success"));
                setDeleteModalState({ isOpen: false, field: null });
                tableFetchDataUtils.refetchData();
            },
            onError: () => {
                console.log(tU("messages.delete_error"));
            },
        });
    };

    const columnConfig: TableColumnType<IntegrationCustomFieldData>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.parameter")} />,
            dataIndex: "parameter",
            key: "parameter",
            inputType: "text",
            // Not inline-editable: parameter must go through the tag-only picker in the drawer
            // form so it stays constrained to predefined/custom_-prefixed keys.
            editable: false,
            width: "33%",
            render: (_, record) => (
                <Typography.Text
                    ellipsis={{ tooltip: record.parameter }}
                    copyable={{
                        text: record.parameter,
                        tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                    }}
                >
                    {record.parameter}
                </Typography.Text>
            ),
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("value")} />,
            dataIndex: "value",
            key: "value",
            inputType: "text",
            editable: true,
            width: "33%",
            render: (_, record) =>
                record.value ? (
                    <Typography.Text
                        ellipsis={{ tooltip: record.value }}
                        copyable={{
                            text: record.value,
                            tooltips: [tC("copy_tooltips.click_to_copy"), tC("copy_tooltips.copied")],
                        }}
                    >
                        {record.value}
                    </Typography.Text>
                ) : (
                    tU("no_value")
                ),
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("description")} />,
            dataIndex: "description",
            key: "description",
            inputType: "text",
            editable: true,
            width: "34%",
            // This table's custom EditableCell renders `children` raw, bypassing antd's built-in
            // `ellipsis` cell wrapping — truncate manually (the column `width` alone constrains the
            // <td>, but `truncate` needs `overflow:hidden` on its own box to actually clip).
            render: (description: string | null) => (
                <div className="truncate" title={description ?? undefined}>
                    {description}
                </div>
            ),
        },
    ];

    const actions: TableAction<IntegrationCustomFieldData>[] = [
        {
            type: "customButton",
            icon: <EditOutlined />,
            onClick: (record) => {
                show(DrawerId.AddApiToolCustomField, { integrationId: integration.id, customFieldId: record.id });
            },
        },
        {
            type: "delete",
            onClick: (record) => {
                setDeleteModalState({ isOpen: true, field: record });
            },
        },
    ];

    return (
        <>
            {integration.id && (
                <AddApiToolCustomFieldDrawer
                    onSubmit={() => tableFetchDataUtils.refetchData()}
                    integrationId={integration.id}
                />
            )}
            <DeleteModal
                open={deleteModalState.isOpen}
                onCancel={() => setDeleteModalState({ isOpen: false, field: null })}
                onConfirm={() => handleDelete(deleteModalState.field?.id)}
                title={deleteModalState.field?.parameter}
            />
            <Table<GetIntegrationCustomFieldsResponse, IntegrationCustomFieldData>
                tableId={tableId}
                data={tableFetchDataUtils.tableData as GetIntegrationCustomFieldsResponse}
                dataSource={tableFetchDataUtils.tableData?.integrationCustomFields || []}
                columnConfig={columnConfig}
                actions={actions}
                loading={!getIntegrationsQuery.data?.integrationCustomFields}
                addEntityData={{
                    drawerId: DrawerId.AddApiToolCustomField,
                    permission: PermissionEnum.IntegrationCustomFieldCreate,
                }}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                defaultOnUpdate={(record) => handleUpdate(record)}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                enableColumnsVisibility={false}
                enableDragAndDrop={false}
                // No `x` here (unlike the Table default of "max-content") — that mode auto-sizes
                // the table to its content, which fights the percentage/fixed column widths below.
                // An empty scroll config keeps the table at 100% of its container instead.
                scroll={{}}
                rowKey={(record) => record.id}
                actionsWidth={60}
                tableLayout="fixed"
            />
        </>
    );
};
